import type { SessionDto } from "@bafe/api-client";
import { canViewAllHubTasks, type WorkspaceApp } from "@bafe/types";
import { AI_PRODUCT_NAME, KeyMark } from "@bafe/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type MouseEvent } from "react";
import { Envelope2, Menu, Search, Sparkles } from "reicon-react";
import { Link, Outlet, useLocation, useMatches, useNavigate, useParams } from "react-router";

import { api } from "../lib/api";
import { presentError } from "../lib/errors";
import { isMailOnlyOrganisationRole, WEBMAIL_HOME } from "../lib/mail-only-member.js";
import { webmailInboxUrl } from "../lib/webmail.js";
import { bindHubScrollFade } from "../lib/hub-scroll.js";
import { useSession } from "../lib/session";
import { isDarkTheme, useOrganisationStore, useThemeStore } from "../lib/stores";
import { WorkspaceNeeded, needsWorkspace } from "../routes/shell/WorkspacePages";
import { PlatformWorkspaceNav, UserNav, WorkspaceSidebarNav } from "./WorkspaceNav";
import { PlatformAccountMenu } from "./PlatformAccountMenu.js";
import { WorkspaceLogo } from "./WorkspaceLogo";
import {
	appRoutePath,
	canOpenPlatformWorkspace,
	isManageWorkspacePath,
	isPersonalAccountPath,
	persistWorkspaceApp,
	readPersistedWorkspaceApp,
	realmOf,
	shouldPersistWorkspaceApp,
	sidebarWorkspaceApp,
	type PlatformRealm,
	type WorkspaceProductApp,
	workspaceAppOf,
	workspaceSwitcherActiveOrganisationId,
} from "../lib/platform-navigation.js";
import { isUserDashboardRoute, USER_DASHBOARD_HOME } from "../lib/user-dashboard.js";
import { parseWorkspaceLocation, workspaceHref, workspaceRestPath, webmailHref } from "../lib/workspace-url.js";

const REALM_LABEL: Record<PlatformRealm, string> = {
	platform: "Platform Workspace",
	workspace: "Workspace",
};

const APP_LABEL: Record<WorkspaceProductApp, string> = {
	manage: "Manage",
	hub: "Hub",
	client: "Client",
	mail: "Mail",
	team: "Team",
	phone: "Phone",
	social: "Social",
	engage: "Engage",
	site: "Site",
};

function isAiPath(pathname: string) {
	const path = appRoutePath(pathname);
	return path === "/ai" || path.startsWith("/ai/");
}

export function PlatformShell({ session }: { session: SessionDto }) {
	const location = useLocation();
	const navigate = useNavigate();
	const matches = useMatches();
	const queryClient = useQueryClient();
	const organisationId = useOrganisationStore((state) => state.organisationId);
	const setOrganisationId = useOrganisationStore((state) => state.setOrganisationId);
	const theme = useThemeStore((state) => state.preference);
	const setTheme = useThemeStore((state) => state.setPreference);
	const [navOpen, setNavOpen] = useState(false);
	const sideRef = useRef<HTMLDivElement>(null);
	const barRef = useRef<HTMLDivElement>(null);
	const lastArea = useRef<WorkspaceProductApp>(readPersistedWorkspaceApp());
	const [switchError, setSwitchError] = useState<string | null>(null);

	const title =
		[...matches]
			.reverse()
			.map((match) => (match.handle as { title?: string } | undefined)?.title)
			.find((value) => value) ?? "Key";

	const routePath = appRoutePath(location.pathname);
	const workspaceLocation = parseWorkspaceLocation(routePath);
	const workspaceMembership =
		workspaceLocation !== null
			? session.memberships.find((membership) => membership.organisationSlug === workspaceLocation.slug)
			: undefined;
	const current =
		workspaceMembership ??
		session.memberships.find((membership) => membership.organisationId === organisationId) ??
		session.memberships[0];
	const inWorkspaceRoute = workspaceLocation !== null;
	const onUserDashboard = isUserDashboardRoute(location.pathname);
	const workspaceSlug = workspaceLocation?.slug ?? "";
	const mailOnlyMember = current ? isMailOnlyOrganisationRole(current.role) : false;
	const realm = realmOf(location.pathname);
	const pathArea = workspaceAppOf(location.pathname);
	const manageView = isManageWorkspacePath(location.pathname);
	if (shouldPersistWorkspaceApp(location.pathname)) {
		lastArea.current = pathArea;
		persistWorkspaceApp(pathArea);
	}
	const sidebarArea = sidebarWorkspaceApp(location.pathname, pathArea, lastArea.current);
	const showPlatformWorkspace = canOpenPlatformWorkspace({ platformOwner: session.platformOwner });
	const switcherActiveOrganisationId =
		workspaceSwitcherActiveOrganisationId(location.pathname, session.memberships) ?? "";
	const linkedInSignIn = location.pathname.includes("/engage/linkedin-sign-in/");
	const appLabel =
		realm === "platform"
			? REALM_LABEL.platform
			: onUserDashboard || isPersonalAccountPath(location.pathname)
				? "Account"
				: APP_LABEL[manageView ? "manage" : sidebarArea];

	useEffect(() => {
		document.title = `${appLabel} · ${title}`;
	}, [appLabel, title]);

	useEffect(() => {
		const side = sideRef.current;
		const bar = barRef.current;
		if (!side || !bar) return;
		const place = () => {
			const max = side.scrollHeight - side.clientHeight;
			if (max <= 1) {
				bar.classList.remove("is-on");
				return;
			}
			const height = Math.max(28, (side.clientHeight / side.scrollHeight) * side.clientHeight);
			const top = (side.scrollTop / max) * (side.clientHeight - height);
			bar.style.height = `${height}px`;
			bar.style.transform = `translateY(${top}px) scaleX(0.5)`;
			bar.classList.add("is-on");
		};
		place();
		side.addEventListener("scroll", place, { passive: true });
		const resize = new ResizeObserver(place);
		resize.observe(side);
		const mutations = new MutationObserver(place);
		mutations.observe(side, { childList: true, subtree: true });
		return () => {
			side.removeEventListener("scroll", place);
			resize.disconnect();
			mutations.disconnect();
		};
	}, [location.pathname]);

	useEffect(() => {
		let timer = 0;
		let pending: HTMLDetailsElement | null = null;
		const menuOf = (target: EventTarget | null) => {
			if (!(target instanceof Element)) return null;
			const details = target.closest("details.select");
			return details instanceof HTMLDetailsElement ? details : null;
		};
		const onOut = (event: globalThis.MouseEvent) => {
			const details = menuOf(event.target);
			if (!details?.open) return;
			if (details.classList.contains("hub-field-select")) return;
			const next = event.relatedTarget;
			if (!(next instanceof Node)) return;
			if (details.contains(next)) return;
			pending = details;
			window.clearTimeout(timer);
			timer = window.setTimeout(() => {
				if (pending) pending.open = false;
				pending = null;
			}, 160);
		};
		const onOver = (event: globalThis.MouseEvent) => {
			const details = menuOf(event.target);
			if (details && details === pending) {
				window.clearTimeout(timer);
				pending = null;
			}
		};
		const onClick = (event: globalThis.MouseEvent) => {
			const details = menuOf(event.target);
			const picked = event.target instanceof Element ? event.target.closest(".select-pop .select-item") : null;
			if (details && picked) details.open = false;
		};
		document.addEventListener("mouseout", onOut);
		document.addEventListener("mouseover", onOver);
		document.addEventListener("click", onClick);
		return () => {
			window.clearTimeout(timer);
			document.removeEventListener("mouseout", onOut);
			document.removeEventListener("mouseover", onOver);
			document.removeEventListener("click", onClick);
		};
	}, []);

	useEffect(() => {
		setNavOpen(false);
	}, [location.pathname]);

	useEffect(() => {
		const open = () => setNavOpen(true);
		window.addEventListener("bafe:open-nav", open);
		return () => window.removeEventListener("bafe:open-nav", open);
	}, []);

	useEffect(() => {
		document.body.style.overflow = navOpen ? "hidden" : "";
		return () => {
			document.body.style.overflow = "";
		};
	}, [navOpen]);

	const restPath = workspaceRestPath(routePath);
	const hubTasksBoard =
		realm === "workspace" &&
		sidebarArea === "hub" &&
		(restPath === "/hub/tasks"
			|| restPath.startsWith("/hub/tasks/")
			|| restPath === "/hub/my/tasks"
			|| restPath.startsWith("/hub/my/tasks/"));
	const teamFrame = realm === "workspace" && (sidebarArea === "team" || sidebarArea === "engage");
	const viewportFrame = teamFrame || hubTasksBoard;

	useEffect(() => {
		if (!viewportFrame) return;
		const root = document.documentElement;
		const sync = () => {
			const viewport = window.visualViewport;
			const height = viewport?.height ?? window.innerHeight;
			const offset = viewport?.offsetTop ?? 0;
			root.style.setProperty("--hub-frame", `${Math.round(height)}px`);
			root.style.setProperty("--hub-frame-offset", `${Math.round(offset)}px`);
			if (offset > 0) window.scrollTo(0, 0);
		};
		const syncAfterKeyboard = (event: FocusEvent) => {
			const target = event.target;
			if (!(target instanceof HTMLElement)) return;
			if (!target.matches("#engage-reply, .team-compose input, .team-compose textarea")) return;
			sync();
			requestAnimationFrame(sync);
			window.setTimeout(sync, 120);
			window.setTimeout(sync, 320);
		};
		sync();
		window.visualViewport?.addEventListener("resize", sync);
		window.visualViewport?.addEventListener("scroll", sync);
		window.addEventListener("resize", sync);
		document.addEventListener("focusin", syncAfterKeyboard);
		return () => {
			window.visualViewport?.removeEventListener("resize", sync);
			window.visualViewport?.removeEventListener("scroll", sync);
			window.removeEventListener("resize", sync);
			document.removeEventListener("focusin", syncAfterKeyboard);
			root.style.removeProperty("--hub-frame");
			root.style.removeProperty("--hub-frame-offset");
		};
	}, [viewportFrame]);

	useEffect(() => {
		if (!navOpen) return;
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") setNavOpen(false);
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [navOpen]);

	function closeNavFromLink(event: MouseEvent<HTMLElement>) {
		const target = event.target;
		if (target instanceof Element && target.closest("a")) setNavOpen(false);
	}
	const apps = new Set(session.apps ?? []);
	const viewAllHubTasks = current ? canViewAllHubTasks(current.role, current.viewAllHubTasks === true) : false;
	const showHubStaff = current ? current.role !== "user" : true;

	async function activateWorkspace(nextId: string): Promise<string | null> {
		setSwitchError(null);
		const result = await api.selectWorkspace(nextId);
		setOrganisationId(result.organisationId);
		await queryClient.invalidateQueries();
		return result.organisationSlug;
	}

	async function manageWorkspace(nextId: string) {
		try {
			const target =
				session.memberships.find((membership) => membership.organisationId === nextId) ?? current;
			let slug = target?.organisationSlug ?? workspaceSlug;
			if (nextId !== (current?.organisationId ?? organisationId)) {
				slug = (await activateWorkspace(nextId)) ?? slug;
			}
			setNavOpen(false);
			if (slug) navigate(workspaceHref(slug, "manage"));
		} catch (error) {
			setSwitchError(presentError(error).title);
		}
	}

	function openPlatformWorkspace() {
		navigate("/platform/workspace");
		setNavOpen(false);
	}

	async function switchWorkspace(nextId: string) {
		try {
			const slug = await activateWorkspace(nextId);
			if (mailOnlyMember) {
				window.location.assign(slug ? webmailHref(slug) : WEBMAIL_HOME);
				return;
			}
			if (slug) navigate(workspaceHref(slug, ""));
			setNavOpen(false);
		} catch (error) {
			setSwitchError(presentError(error).title);
		}
	}

	async function signOut() {
		try {
			await api.logout();
		} finally {
			window.location.assign("/signin");
		}
	}

	const hubRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const hub = hubRef.current;
		if (!hub) return;
		return bindHubScrollFade(hub);
	}, []);

	return (
		<div
			ref={hubRef}
			className={`hub${navOpen ? " nav-open" : ""}${viewportFrame ? " hub-viewport-sync" : ""}${teamFrame ? " team-open" : ""}${linkedInSignIn ? " linkedin-signin" : ""}`}
		>
			<button type="button" className="side-mask" aria-label="Close menu" onClick={() => setNavOpen(false)} />
			<aside className="side" onClick={closeNavFromLink}>
				<div className="brand">
					<Link to={USER_DASHBOARD_HOME} className="brand-home" aria-label="User dashboard">
						{session.memberships.length === 0 ? (
							<span className="who">{session.platformOwner ? "Key" : "Key"}</span>
						) : (
							<KeyMark className="side-mark" title="Key" />
						)}
					</Link>
					<button type="button" className="side-search" aria-label="Search">
						<Search size={16} aria-hidden="true" />
					</button>
				</div>

				<div className="side-scroll" ref={sideRef}>
				<div className="side-bar" ref={barRef} aria-hidden="true" />
				<nav aria-label="Workspace">
					{realm === "platform" ? (
						<PlatformWorkspaceNav pathname={location.pathname} />
					) : null}
					{onUserDashboard ? <UserNav pathname={routePath} /> : null}
					{realm === "workspace" && inWorkspaceRoute && workspaceSlug ? (
						mailOnlyMember ? (
							<WorkspaceSidebarNav
								pathname={location.pathname}
								workspaceSlug={workspaceSlug}
								apps={apps}
								mailOnly
							/>
						) : (
							<WorkspaceSidebarNav
								pathname={location.pathname}
								workspaceSlug={workspaceSlug}
								apps={apps}
								viewAllHubTasks={viewAllHubTasks}
								showHubStaff={showHubStaff}
							/>
						)
					) : null}
				</nav>
				</div>

				<div className="side-account">
					{switchError && <p className="err">{switchError}</p>}
					<PlatformAccountMenu
						name={session.user.fullName}
						email={session.user.email}
						settingsHref="/user/settings/profile"
						onSettingsClick={() => navigate("/user/settings/profile")}
						onSignOut={() => void signOut()}
						darkMode={isDarkTheme(theme)}
						onDarkModeChange={(dark) => setTheme(dark ? "dark" : "light")}
						memberships={session.memberships}
						activeOrganisationId={switcherActiveOrganisationId}
						onSwitchWorkspace={(nextId) => void switchWorkspace(nextId)}
						onManageWorkspace={(nextId) => void manageWorkspace(nextId)}
						onPlatformWorkspace={showPlatformWorkspace ? openPlatformWorkspace : undefined}
						platformWorkspaceActive={realm === "platform"}
						onUserDashboard={() => {
							navigate(USER_DASHBOARD_HOME);
							setNavOpen(false);
						}}
						userDashboardActive={onUserDashboard}
					/>
				</div>
			</aside>

			<div className="main">
				<header className="top">
					<button type="button" className="side-toggle" aria-label="Open menu" onClick={() => setNavOpen(true)}>
						<Menu size={18} aria-hidden="true" />
					</button>
					{inWorkspaceRoute && current ? (
						<WorkspaceLogo name={current.organisationName} logo={current.logo} />
					) : null}
					<h1 className="top-title">{title}</h1>
					<div className="top-actions">
						<Link className={isAiPath(location.pathname) ? "top-link active" : "top-link"} to="/ai">
							<Sparkles size={16} aria-hidden="true" />
							<span>{AI_PRODUCT_NAME}</span>
						</Link>
						{inWorkspaceRoute ? (
							<a className="top-link" href={webmailInboxUrl(workspaceSlug || undefined)}>
								<Envelope2 size={16} aria-hidden="true" />
								<span>Webmail</span>
							</a>
						) : null}
					</div>
				</header>
				<div className="wrap" id="main">
					{session.memberships.length === 0 && needsWorkspace(location.pathname) ? (
						<WorkspaceNeeded platformOwner={session.platformOwner} />
					) : (
						<Outlet />
					)}
				</div>
				<footer className="foot">© {new Date().getFullYear()} Key</footer>
			</div>
		</div>
	);
}

export function AppGate({ app }: { app: WorkspaceApp | "client" | "phone" }) {
	const session = useSession();
	const { workspaceSlug = "" } = useParams();
	const label = APP_LABEL[app as WorkspaceProductApp] ?? app;
	const apps = session.data?.apps ?? [];
	const required = app === "client" || app === "phone" ? "hub" : app;
	if (session.data && !apps.includes(required)) {
		return (
			<p className="empty">
				{label} is not turned on for this workspace.{" "}
				<Link to={workspaceSlug ? workspaceHref(workspaceSlug, "manage/apps") : "/user/workspaces"}>
					Choose apps in Manage.
				</Link>
			</p>
		);
	}
	return <Outlet />;
}
